import React from "react";
import { motion } from "framer-motion";
import {
  ArrowForwardRounded, BoltRounded, CalendarTodayRounded, CheckCircleRounded,
  MoreHorizRounded, NearMeRounded, SchoolRounded, TrendingUpRounded
} from "@mui/icons-material";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell
} from "recharts";
const empty={user:{firstName:"FDC",branch:"",status:""},kpis:[],target:{achieved:0,goal:20,percent:0,visitsToday:0,followUps:0},wallet:{available:"—",pending:"—",lifetime:"—"},stateDevelopmentDirector:{name:"",role:"SDD",remark:""},acquisition:[],clientStatus:[],todayTargets:[],activity:[],training:{percent:0,completed:0,inProgress:0}};

const PIE_COLORS = ["#3a84f8", "#6d5dfc", "#f4b740", "#98a2b3", "#ef6b73"];

export default function FDCDashboard({ onNavigate, apiData, loading, error, onRetry }) {
  const data={...empty,...apiData?.ui};
  const { stateDevelopmentDirector, acquisition, clientStatus, todayTargets, activity, training } = data;
  const apiStaff = apiData?.staff;
  const nameParts = String(apiStaff?.name || "").trim().split(/\s+/).filter(Boolean);
  const user = {
    ...data.user,
    firstName: apiStaff?.personalInfo?.firstName || nameParts[0] || data.user.firstName,
    branch: apiStaff?.assignment?.branch || data.user.branch,
    status: apiStaff?.employment?.status || data.user.status,
  };
  const clients = apiData?.clients || {};
  const apiTarget = apiData?.target || {};
  const earnings = apiData?.earnings || {};
  const goal = Number(apiTarget.monthlyMinimum || 20);
  const achieved = Number(apiTarget.achievedThisMonth || 0);
  const percent = goal > 0 ? Math.round((achieved / goal) * 100) : 0;
  const money = (value) => new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(Number(value || 0));
  const kpis = apiData ? [
    { key:"clients", label:"Total clients", value:String(clients.total || 0), delta:`${clients.today || 0} registered today`, tone:"blue" },
    { key:"month", label:"Registered this month", value:String(clients.thisMonth || 0), delta:`${clients.thisYear || 0} this year`, tone:"green" },
    { key:"target", label:"Monthly target", value:`${achieved} / ${goal}`, delta:`${percent}% achieved`, tone:"purple" },
    { key:"earnings", label:"Wallet balance", value:money(earnings.balance), delta:`${money(earnings.commissionPerClient)} per client`, tone:"amber" },
  ] : data.kpis;
  const target = apiData ? { ...data.target, achieved, goal, percent } : data.target;
  const wallet = apiData ? { ...data.wallet, available: money(earnings.balance), pending:data.wallet.pending, lifetime:data.wallet.lifetime } : data.wallet;
  return (
    <div className="fdcDashboard">
      {loading && <div className="fdcApiState">Loading live FDC dashboard data...</div>}
      {error && <div className="fdcApiState error"><span>{error}</span><button type="button" onClick={onRetry}>Retry</button></div>}
      <section className="fdcWelcome">
        <div>
          <div className="fdcWelcomeMeta"><span className="fdcStatusDot" /> {user.status} · {user.branch}</div>
          <h2>Welcome, {user.firstName}.</h2>
          <p>You have <strong>{target.visitsToday} market visits</strong> and <strong>{target.followUps} follow-ups</strong> lined up today.</p>
        </div>
        <div className="fdcWelcomeActions">
          <button onClick={() => onNavigate("onboarding")} className="fdcQuickPrimary"><BoltRounded/> Start registration</button>
          <button onClick={() => onNavigate("targets")} className="fdcQuickSecondary"><NearMeRounded/> Today's route</button>
        </div>
      </section>

      <section className="fdcKpiGrid">
        {kpis.map((kpi, i) => (
          <motion.article className="fdcKpiCard" key={kpi.key}
            initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} transition={{delay:i*.05}}>
            <div className="fdcKpiTop"><span>{kpi.label}</span><button className="fdcTinyButton" onClick={()=>onNavigate(kpi.key==="earnings"?"wallet":kpi.key==="target"?"performance":"clients")}><MoreHorizRounded/></button></div>
            <strong className="fdcKpiValue">{kpi.value}</strong>
            <p className={`fdcKpiDelta ${kpi.tone}`}><TrendingUpRounded /> {kpi.delta}</p>
          </motion.article>
        ))}
      </section>

      <section className="fdcDashboardGrid fdcDashboardGridPrimary">
        <article className="fdcPanel fdcAnalyticsPanel">
          <div className="fdcPanelHead">
            <div><span className="fdcPanelKicker">Growth</span><h3>Client acquisition</h3><p>Registrations and successful conversions.</p></div>
            <select aria-label="Analytics period"><option>Last 6 months</option><option>This year</option></select>
          </div>
          <div className="fdcChartWrap">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={acquisition} margin={{top:10,right:10,left:-15,bottom:0}}>
                <defs>
                  <linearGradient id="fdcArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3a84f8" stopOpacity={0.25}/>
                    <stop offset="95%" stopColor="#3a84f8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#eef1f6"/>
                <XAxis dataKey="month" axisLine={false} tickLine={false}/>
                <YAxis axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{borderRadius:"12px",border:"1px solid #e8edf4"}}/>
                <Area type="monotone" dataKey="registrations" stroke="#3a84f8" strokeWidth={3} fill="url(#fdcArea)"/>
                <Area type="monotone" dataKey="conversions" stroke="#6d5dfc" strokeWidth={2} fillOpacity={0}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="fdcPanel">
          <div className="fdcPanelHead"><div><span className="fdcPanelKicker">Portfolio</span><h3>Client status</h3><p>Health of your acquired clients.</p></div></div>
          <div className="fdcStatusContent">
            <div className="fdcDonut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={clientStatus} dataKey="value" innerRadius={58} outerRadius={76} paddingAngle={3}>
                  {clientStatus.map((x,i)=><Cell key={x.name} fill={PIE_COLORS[i]}/>)}
                </Pie></PieChart>
              </ResponsiveContainer>
              <div className="fdcDonutCenter"><strong>{clients.total || 0}</strong><span>clients</span></div>
            </div>
            <div className="fdcLegend">{clientStatus.map((x,i)=>
              <div key={x.name}><span><i style={{background:PIE_COLORS[i]}} />{x.name}</span><strong>{x.value}</strong></div>)}
            </div>
          </div>
        </article>
      </section>

      <section className="fdcDashboardGrid fdcDashboardGridSecondary">
        <article className="fdcPanel">
          <div className="fdcPanelHead"><div><span className="fdcPanelKicker">Execution</span><h3>Monthly target</h3></div>
            <button className="fdcTextButton" onClick={()=>onNavigate("performance")}>View performance <ArrowForwardRounded/></button></div>
          <div className="fdcTargetHero"><strong>{target.percent}%</strong><span>of monthly target</span></div>
          <div className="fdcProgress"><span style={{width:`${Math.min(100,target.percent)}%`}} /></div>
          <div className="fdcTargetStats"><div><strong>{target.achieved}</strong><span>Acquired</span></div><div><strong>{Math.max(0,target.goal-target.achieved)}</strong><span>Remaining</span></div><div><strong>{target.goal}</strong><span>Target</span></div></div>
        </article>

        <article className="fdcPanel fdcWalletCard">
          <div className="fdcPanelHead"><div><span className="fdcPanelKicker">Earnings</span><h3>Wallet</h3></div>
            <button className="fdcTextButton" onClick={()=>onNavigate("wallet")}>Open wallet <ArrowForwardRounded/></button></div>
          <span className="fdcWalletLabel">Available balance</span><strong className="fdcWalletBalance">{wallet.available}</strong>
          <div className="fdcWalletMeta"><div><span>Pending</span><strong>{wallet.pending}</strong></div><div><span>Total earned</span><strong>{wallet.lifetime}</strong></div></div>
        </article>

        <article className="fdcPanel">
          <div className="fdcPanelHead"><div><span className="fdcPanelKicker">Learning</span><h3>Training progress</h3></div><SchoolRounded className="fdcPanelIcon"/></div>
          <div className="fdcTrainingRing" style={{"--progress":`${training.percent * 3.6}deg`}}><div><strong>{training.percent}%</strong><span>complete</span></div></div>
          <div className="fdcTrainingMeta"><span><CheckCircleRounded/> {training.completed} completed</span><span>{training.inProgress} in progress</span></div>
          <button className="fdcWideButton" onClick={()=>onNavigate("training")}>Continue training</button>
        </article>
      </section>

      <section className="fdcDashboardGrid fdcDashboardGridBottom">
        <article className="fdcPanel">
          <div className="fdcPanelHead"><div><span className="fdcPanelKicker">Field work</span><h3>Today's targets</h3><p>Your next market activities.</p></div>
            <button className="fdcTextButton" onClick={()=>onNavigate("targets")}>All targets <ArrowForwardRounded/></button></div>
          <div className="fdcTargetList">
            {todayTargets.map(t=><div className="fdcTargetRow" key={t.id}>
              <div className="fdcTargetTime"><CalendarTodayRounded/><span>{t.time}</span></div>
              <div className="fdcTargetBusiness"><strong>{t.business}</strong><span>{t.category} · {t.area}</span></div>
              <span className={`fdcPriority ${t.priority.toLowerCase()}`}>{t.priority}</span>
              <button className="fdcRowAction" onClick={()=>onNavigate("targets",{target:t.id})}>Open <ArrowForwardRounded/></button>
            </div>)}
          </div>
        </article>

        <article className="fdcPanel">
          <div className="fdcPanelHead"><div><span className="fdcPanelKicker">Live feed</span><h3>Recent activity</h3></div></div>
          <div className="fdcTimeline">{activity.map((a,i)=><div className="fdcTimelineItem" key={a.id}>
            <div className="fdcTimelineRail"><span />{i<activity.length-1 && <i/>}</div>
            <div><strong>{a.title}</strong><p>{a.detail}</p><small>{a.time}</small></div>
          </div>)}</div>
        </article>
      </section>

      <section className="fdcState Development DirectorBanner">
        <div className="fdcState Development DirectorIdentity"><div className="fdcAvatar large">AI</div><div><span className="fdcPanelKicker">Your State Development Director</span><h3>{stateDevelopmentDirector.name}</h3><p>{stateDevelopmentDirector.role}</p></div></div>
        <blockquote>“{stateDevelopmentDirector.remark}”</blockquote>
        <div className="fdcState Development DirectorActions"><button className="fdcQuickSecondary" onClick={()=>onNavigate("messages")}>Message</button><button className="fdcQuickPrimary" onClick={()=>onNavigate("administration")}>View organization</button></div>
      </section>
    </div>
  );
}
